import React, { useState } from 'react';
import cn from 'classnames';

import PageLayout from 'components/common/PageLayout';
import {
  ANALYTICS_ROUTE,
  ENTERPRISE_CONTACT_LINK,
  INTEGRATIONS_ROUTE,
  STUDIO_ROUTE,
  SUBSCRIPTION_PLANS_PAGE,
} from 'components/constants';
import { LinkWithQuery } from 'components/common/LinkWithQuery';

import { figmaToStorycardsFaqs } from './data';
import css from './styles.module.scss';

type CommentExample = {
  id: 'motion' | 'slider' | 'logic';
  label: string;
  comment: string;
  layer: string;
  settings: string[];
};

const commentExamples: CommentExample[] = [
  {
    id: 'motion',
    label: 'Motion',
    comment: 'Bring the star in from the right, then let it fade out.',
    layer: 'Star / celebration',
    settings: ['Enter from right', 'Duration 900 ms', 'Fade after 1.8 s'],
  },
  {
    id: 'slider',
    label: 'Slider',
    comment: 'Make this slider advance every five seconds.',
    layer: 'Recommendations / slider',
    settings: ['Autoplay on', 'Interval 5.0 s', 'Editable in Studio'],
  },
  {
    id: 'logic',
    label: 'Interaction',
    comment: 'Reveal the result after the user selects an answer.',
    layer: 'Result / panel',
    settings: ['Trigger on answer', 'Reveal result layer', 'Editable in Studio'],
  },
];

const intentCards = [
  {
    number: '01',
    title: 'Canvas',
    text: 'Layout, typography, color and images provide the visual context for the build.',
  },
  {
    number: '02',
    title: 'Attached comments',
    text: 'Instructions stay connected to the elements they describe, including motion, timing and interaction.',
  },
  {
    number: '03',
    title: 'Studio build',
    text: 'The result opens as structured layers that your team can review and refine before publishing.',
  },
];

const workflowSteps = [
  {
    number: '01',
    title: 'Connect your Figma file',
    text: 'Connect the file in Storycards AI Chat and add any product context the build needs. No plugin is required.',
  },
  {
    number: '02',
    title: 'Read canvas and comments together',
    text: 'Storycards reads the visual structure and the comments attached to specific elements, then uses both to build the interaction.',
  },
  {
    number: '03',
    title: 'Review and refine in Studio',
    text: 'Open the result as editable Studio layers. Refine responsive behavior, connect data and logic, then publish when ready.',
  },
];

const editableInputs = [
  ['Layer structure', 'Edit groups, hierarchy and the relationships between visual elements.'],
  ['Typography and styles', 'Refine type, color, spacing and visual hierarchy.'],
  ['Images and assets', 'Replace or reposition visual content from the original design.'],
  ['Responsive layouts', 'Tune layout, type and content for each supported breakpoint.'],
  ['Motion and timing', 'Adjust entrances, exits, delays, duration and autoplay behavior.'],
  ['Interaction logic', 'Edit states, triggers and responses that turn the design into a product.'],
];

const StudioWindow = ({ compact = false }: { compact?: boolean }) => (
  <div
    className={cn(css.studioWindow, compact && css.compactStudio)}
    role="img"
    aria-label="Storycards Studio preview with editable layers"
  >
    <div className={css.windowBar}>
      <div className={css.windowDots} aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <span>Storycards Studio</span>
      <span className={css.savedState}>Saved</span>
    </div>
    <div className={css.studioWorkspace}>
      <div className={css.toolRail} aria-hidden="true">
        <span>+</span>
        <span>◇</span>
        <span>T</span>
      </div>
      <div className={css.studioCanvas}>
        <div className={css.productCard}>
          <span className={css.productKicker}>YOUR MATCH</span>
          <strong>Built around your choices.</strong>
          <span className={css.productLine} />
          <span className={css.productButton}>See the result</span>
          <span className={css.heroStar} aria-hidden="true">
            ✦
          </span>
        </div>
      </div>
      <div className={css.layersPanel}>
        <strong>Layers</strong>
        <span>⌄ Result card</span>
        <span className={css.activeLayer}>✦ Star</span>
        <span>T Heading</span>
        <span>▭ Button</span>
        <span>▧ Background</span>
      </div>
    </div>
  </div>
);

export default function FigmaToStorycards() {
  const [activeExample, setActiveExample] = useState<CommentExample>(commentExamples[0]);

  return (
    <PageLayout className={css.page} isFullWidth isWhiteHeader={false} showFooterCta={false}>
      <main>
        <section className={css.hero}>
          <div className={css.heroCopy}>
            <span className={css.eyebrow}>Figma to Storycards</span>
            <h1>Turn Figma designs into interactive products.</h1>
            <p>
              Connect your Figma file in Storycards AI Chat. Storycards reads the canvas and the comments attached to
              its elements, builds the motion and behavior your designers described, and opens the result in Studio as
              editable layers.
            </p>
            <div className={css.heroActions}>
              <a href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noreferrer" className={css.primaryAction}>
                Start Creating
              </a>
              <a href="#how-it-works" className={css.secondaryAction}>
                See how it works
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>

          <div
            className={css.heroVisual}
            role="img"
            aria-label="Figma designs and comments translated into editable Storycards layers"
          >
            <div className={css.ambientGlow} />
            <div className={css.figmaWindow}>
              <div className={css.windowBar}>
                <div className={css.windowDots} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <span>Figma · Campaign concept</span>
                <span>100%</span>
              </div>
              <div className={css.figmaCanvas}>
                <div className={css.figmaFrame}>
                  <span className={css.frameLabel}>RESULT / DESKTOP</span>
                  <div className={css.figmaCard}>
                    <span>YOUR MATCH</span>
                    <strong>Built around your choices.</strong>
                    <i />
                    <b>See the result</b>
                    <span className={css.figmaStar}>✦</span>
                  </div>
                  <span className={css.commentPin}>1</span>
                </div>
                <div className={css.commentBubble}>
                  <div>
                    <span className={css.avatar}>M</span>
                    <p>
                      <strong>Maya</strong>
                      <small>Designer</small>
                    </p>
                  </div>
                  <p>Bring the star in from the right, then let it fade out.</p>
                  <span className={css.commentStatus}>Instruction detected</span>
                </div>
              </div>
            </div>

            <div className={css.aiBridge}>
              <span className={css.aiPulse} aria-hidden="true" />
              <strong>Storycards AI Chat</strong>
              <small>Reads canvas + attached comments</small>
            </div>

            <StudioWindow />
          </div>

          <div className={css.heroFootnote}>
            <span>No Figma plugin required</span>
            <span>Comments interpreted in context</span>
            <span>Editable Studio layers</span>
          </div>
        </section>

        <section className={css.intentSection}>
          <div className={css.sectionIntro}>
            <span className={css.eyebrow}>Design and intent</span>
            <h2>Bring the design and its behavior into the same build.</h2>
            <p>
              A Figma frame shows how the product should look. Comments can describe how an element should move, respond
              or change. Storycards reads both in context and carries that intent into the build.
            </p>
          </div>
          <div className={css.intentGrid}>
            {intentCards.map((card) => (
              <article key={card.number}>
                <span>{card.number}</span>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={css.commentDemoSection} aria-labelledby="comment-demo-title">
          <div className={css.commentDemoIntro}>
            <span className={css.eyebrow}>Figma comments to interactions</span>
            <h2 id="comment-demo-title">Turn designer comments into working interactions.</h2>
            <p>
              Choose a comment to see how an instruction attached to a specific element can become editable behavior in
              Studio.
            </p>
            <div className={css.demoTabs} aria-label="Figma comment examples">
              {commentExamples.map((example) => (
                <button
                  key={example.id}
                  type="button"
                  aria-pressed={activeExample.id === example.id}
                  className={cn(activeExample.id === example.id && css.activeTab)}
                  onClick={() => setActiveExample(example)}
                >
                  {example.label}
                </button>
              ))}
            </div>
          </div>

          <div className={css.demoStage}>
            <div className={css.demoComment}>
              <span className={css.demoCommentNumber}>1</span>
              <span className={css.demoAvatar}>M</span>
              <div>
                <p>{activeExample.comment}</p>
                <span>Comment on {activeExample.layer}</span>
              </div>
            </div>

            <div className={css.demoConnector} aria-hidden="true">
              <span />
              <i>AI</i>
              <span />
            </div>

            <div className={css.demoOutput} aria-live="polite">
              <div className={css.demoOutputHeader}>
                <span>Behavior</span>
                <strong>{activeExample.layer}</strong>
              </div>
              <div className={cn(css.behaviorPreview, css[activeExample.id])}>
                <div className={css.previewCard}>
                  <span className={css.previewStar}>✦</span>
                  <div className={css.sliderCards}>
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className={css.logicChoice}>
                    <span>Your answer</span>
                    <strong>Matched result</strong>
                  </div>
                </div>
              </div>
              <ul>
                {activeExample.settings.map((setting) => (
                  <li key={setting}>
                    <span>✓</span>
                    {setting}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className={css.workflowSection} id="how-it-works">
          <div className={css.sectionIntro}>
            <span className={css.eyebrow}>How it works</span>
            <h2>From a Figma file to an editable Studio build.</h2>
            <p>The handoff starts in Storycards AI Chat and continues in Studio, with no Figma plugin to install.</p>
          </div>
          <div className={css.workflowGrid}>
            {workflowSteps.map((step) => (
              <article key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={css.editableSection}>
          <div className={css.editableVisual}>
            <StudioWindow compact />
          </div>
          <div className={css.editableContent}>
            <span className={css.eyebrow}>Structured Studio layers</span>
            <h2>Continue editing every layer in Studio.</h2>
            <p>
              Storycards creates a structured Studio build, not a screenshot or locked block. Designers can inspect and
              refine layout, styles, responsive behavior, motion and logic before publishing.
            </p>
            <div className={css.editableGrid}>
              {editableInputs.map(([title, text]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={css.afterHandoffSection}>
          <div className={css.sectionIntro}>
            <span className={css.eyebrow}>Continue past the handoff</span>
            <h2>Continue building in Storycards.</h2>
          </div>
          <div className={css.afterHandoffGrid}>
            <article>
              <span>Responsive control</span>
              <h3>Tune each breakpoint.</h3>
              <p>Change layout, type, content and behavior for desktop, tablet and mobile in Studio.</p>
              <LinkWithQuery to={STUDIO_ROUTE}>Explore the Studio →</LinkWithQuery>
            </article>
            <article>
              <span>Data and logic</span>
              <h3>Add logic, data and personalized paths.</h3>
              <p>Connect answers, conditions, API data and forms to the visual design.</p>
              <LinkWithQuery to={INTEGRATIONS_ROUTE}>Explore integrations →</LinkWithQuery>
            </article>
            <article>
              <span>Review and improve</span>
              <h3>Publish with control, then learn from interaction data.</h3>
              <p>
                Review the product, publish when ready and use first-party interaction data to improve it over time.
              </p>
              <LinkWithQuery to={ANALYTICS_ROUTE}>Explore analytics →</LinkWithQuery>
            </article>
          </div>
        </section>

        <section className={css.faqSection} aria-labelledby="figma-faq-title">
          <div>
            <span className={css.eyebrow}>Questions</span>
            <h2 id="figma-faq-title">Figma to Storycards FAQ</h2>
          </div>
          <div className={css.faqList}>
            {figmaToStorycardsFaqs.map((faq) => (
              <details key={faq.question}>
                <summary>
                  {faq.question}
                  <span aria-hidden="true">+</span>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={css.finalCta}>
          <span className={css.eyebrow}>Build from Figma</span>
          <h2>Bring the design and its intent into Studio.</h2>
          <p>
            Connect a Figma file in AI Chat and continue with an interactive product your team can review, refine and
            publish.
          </p>
          <div>
            <a href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noreferrer" className={css.primaryAction}>
              Start Creating
            </a>
            <a href={ENTERPRISE_CONTACT_LINK} className={css.secondaryAction}>
              Talk to our enterprise team
            </a>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
